// Certificates of completion: the certificate itself, the Profile card (claim / open), and the
// public check page #/cert/<ID>. Rules and eligibility: src/core/certificates.js; storage and the
// server calls: src/core/certs.js. Original artwork (RouteZero purple → cyan); the medal artwork is
// RouteZero's own (src/ui/medals-view.js).
import { h } from './h.js';
import { medalArt } from './medals-view.js';
import { symbolSrc, BRAND } from './brand.js';
import { CERTIFICATES, certOf, CERT_ID, normalizeCertId } from '../core/certificates.js';

const MEDAL_ICON = { support: 'headset', d01: 'nodes', d02: 'switch', d03: 'router', d04: 'globe', d05: 'gear', d06: 'shield', d07: 'clipboard', phase1: 'nodes' };
const fmtDate = (t) => new Date(t).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
/** Public link to a certificate on this site. */
export const certUrl = (id) => (typeof location !== 'undefined' ? `${location.origin}${location.pathname}#/cert/${id}` : `#/cert/${id}`);
/** LinkedIn “Add licence or certification” link (opens LinkedIn with the fields filled in). */
export function linkedInUrl(cert) {
  const c = certOf(cert.kind);
  const d = new Date(cert.issued_at);
  const q = new URLSearchParams({ startTask: 'CERTIFICATION_NAME', name: c?.title || 'RouteZero certificate', organizationName: BRAND.name, issueYear: String(d.getFullYear()), issueMonth: String(d.getMonth() + 1), certUrl: certUrl(cert.id), certId: cert.id });
  return `https://www.linkedin.com/profile/add?${q}`;
}

/** The certificate (screen and print). */
export function certificateDoc(cert) {
  const c = certOf(cert.kind);
  return h('article', { class: `rz-cert${cert.kind === 'phase1' ? ' grand' : ''}`, 'data-testid': 'certificate', 'aria-label': `${BRAND.name} certificate ${cert.id}` },
    h('div', { class: 'rz-cert-inner' },
      h('header', { class: 'rz-cert-head' },
        h('img', { src: symbolSrc(192), alt: '', width: 44, height: 44, class: 'rz-cert-logo' }),
        h('span', { class: 'rz-cert-brand' }, h('b', {}, 'Route'), h('i', {}, 'Zero')),
        h('span', { class: 'rz-cert-kicker' }, 'CERTIFICATE OF COMPLETION')),
      h('p', { class: 'rz-cert-pre' }, 'This certifies that'),
      h('h1', { class: 'rz-cert-name', 'data-testid': 'certificate-name' }, cert.holder_name),
      h('p', { class: 'rz-cert-pre' }, 'has successfully completed'),
      h('h2', { class: 'rz-cert-title', 'data-testid': 'certificate-title' }, c?.title || cert.kind),
      h('p', { class: 'rz-cert-detail' }, c?.detail || ''),
      h('footer', { class: 'rz-cert-foot' },
        h('div', { class: 'rz-cert-meta' },
          h('span', {}, h('small', {}, 'ISSUED'), h('b', { 'data-testid': 'certificate-date' }, fmtDate(cert.issued_at))),
          h('span', {}, h('small', {}, 'CERTIFICATE ID'), h('b', { class: 'mono', 'data-testid': 'certificate-id' }, cert.id)),
          h('span', { class: 'rz-cert-check' }, h('small', {}, 'CHECK IT AT'), h('b', { class: 'mono' }, certUrl(cert.id).replace(/^https?:\/\//, '')))),
        h('div', { class: 'rz-cert-seal' }, medalArt({ icon: MEDAL_ICON[cert.kind] || 'nodes' }, { size: 74 })))));
}

/**
 * Profile: every certificate with its state — claim (name to print), open, or progress.
 * @param {{ certs: object[]|null, progress: Record<string,{done:number,total:number,eligible:boolean}>, defaultName: string, onClaim: (kind:string, name:string) => Promise<{ok:boolean,error?:string}>, error?: string|null }} o
 */
export function certificatesCard({ certs, progress, defaultName = '', onClaim, error = null }) {
  const have = new Map((certs || []).map((c) => [c.kind, c]));
  const items = CERTIFICATES.map((c) => {
    const cert = have.get(c.kind);
    const p = progress[c.kind] || { done: 0, total: c.needs.length, eligible: false };
    if (cert) {
      return h('li', { class: 'rz-cert-item issued', 'data-testid': `cert-${c.kind}`, 'data-state': 'issued' },
        h('b', {}, c.short), h('small', {}, `Issued ${fmtDate(cert.issued_at)} · ${cert.id}`),
        h('a', { class: 'btn small primary', href: `#/cert/${cert.id}`, 'data-testid': `cert-open-${c.kind}` }, 'View certificate'));
    }
    if (p.eligible) {
      const input = h('input', { value: defaultName, maxlength: 60, 'aria-label': `Name to print on the ${c.short} certificate`, 'data-testid': `cert-name-${c.kind}` });
      const msg = h('small', { class: 'rz-cert-msg', role: 'status' });
      const btn = h('button', { class: 'btn small primary', type: 'button', 'data-testid': `cert-claim-${c.kind}`, onclick: async () => {
        btn.disabled = true; msg.textContent = 'Issuing…';
        const r = await onClaim(c.kind, input.value);
        if (!r.ok) { btn.disabled = false; msg.textContent = r.error || 'Could not issue the certificate.'; }
      } }, 'Claim certificate');
      return h('li', { class: 'rz-cert-item ready', 'data-testid': `cert-${c.kind}`, 'data-state': 'ready' },
        h('b', {}, c.short), h('small', {}, 'Ready — check the name below. It is printed exactly like this and cannot be changed later.'),
        h('div', { class: 'rz-cert-claim' }, input, btn), msg);
    }
    return h('li', { class: 'rz-cert-item', 'data-testid': `cert-${c.kind}`, 'data-state': 'locked' },
      h('b', {}, c.short), h('small', {}, `${p.done} / ${p.total} verified`),
      h('span', { class: 'rz-cert-bar', role: 'progressbar', 'aria-valuenow': p.done, 'aria-valuemin': 0, 'aria-valuemax': p.total, 'aria-label': `${c.short} progress` }, h('i', { style: { width: `${p.total ? Math.round((p.done / p.total) * 100) : 0}%` } })));
  });
  return h('section', { class: 'rz-career-card panel rz-certs-card', id: 'rz-certs', 'data-testid': 'profile-certificates', 'aria-labelledby': 'rz-certs-title' },
    h('div', { class: 'eyebrow' }, 'CERTIFICATES'),
    h('h2', { id: 'rz-certs-title' }, 'Certificates of completion'),
    h('p', {}, 'Issued from your server-verified work only. Each one has a public link anyone can open to check it is real.'),
    error ? h('p', { class: 'rz-cert-msg', role: 'alert' }, error) : null,
    certs === null ? h('p', { class: 'muted small' }, 'Loading your certificates…') : h('ul', { class: 'rz-cert-list' }, items));
}

/** Public page #/cert/<ID>: checks the ID with the server and shows the certificate. */
export function certificatePage({ code, lookup, signedIn = false }) {
  const id = normalizeCertId(code);
  const status = h('div', { class: 'rz-cert-status', role: 'status' }, 'Checking certificate…');
  const host = h('div', { class: 'rz-cert-host' });
  const page = h('main', { class: 'page rz-cert-page', 'data-testid': 'cert-page' }, status, host);
  const notFound = (why) => {
    status.className = 'rz-cert-status bad'; status.dataset.testid = 'cert-not-found';
    status.replaceChildren(h('b', {}, 'No valid certificate found. '), why);
  };
  if (!CERT_ID.test(id)) { notFound(`“${String(code || '').slice(0, 20)}” is not a RouteZero certificate ID (they look like RZ-7K2Q-M9XD).`); return page; }
  (async () => {
    let cert = null;
    try { cert = await lookup(id); } catch { status.className = 'rz-cert-status bad'; status.textContent = 'Could not reach RouteZero to check this certificate. Try again in a moment.'; return; }
    if (!cert) { notFound(`${id} does not exist or has been revoked.`); return; }
    status.className = `rz-cert-status ${cert.dev ? 'warn' : 'good'}`; status.dataset.testid = 'cert-verified';
    status.replaceChildren(cert.dev
      ? h('span', {}, h('b', {}, 'Development certificate — '), 'issued by a test sign-in in this browser. It is not a real RouteZero certificate.')
      : h('span', {}, h('b', {}, '✓ Verified by RouteZero — '), `${cert.id} was issued to ${cert.holder_name} on ${fmtDate(cert.issued_at)}.`));
    const copy = h('button', { class: 'btn small ghost', type: 'button', 'data-testid': 'cert-copy', onclick: async () => {
      try { await navigator.clipboard.writeText(certUrl(cert.id)); copy.textContent = 'Link copied'; } catch { copy.textContent = certUrl(cert.id); }
    } }, 'Copy link');
    host.replaceChildren(certificateDoc(cert),
      h('div', { class: 'rz-cert-actions' },
        h('button', { class: 'btn small primary', type: 'button', 'data-testid': 'cert-print', onclick: () => window.print() }, 'Print or save as PDF'),
        copy,
        h('a', { class: 'btn small ghost', href: linkedInUrl(cert), target: '_blank', rel: 'noopener noreferrer', 'data-testid': 'cert-linkedin' }, 'Add to LinkedIn'),
        signedIn ? h('a', { class: 'btn small ghost', href: '#/profile' }, 'Back to Profile') : h('a', { class: 'btn small ghost', href: '#/' }, `About ${BRAND.name}`)));
  })();
  return page;
}
